import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import CallMergeRoundedIcon from "@mui/icons-material/CallMergeRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

import useLanguage from "../../hooks/useLanguage";
import useHighlights from "../../hooks/useHighlights";
import useToaster from "../../hooks/useToaster";
import useMediaQuery from "../../hooks/useMediaQuery";
import useMemorize from "../../hooks/useMemorize";
import useUserData from "../../hooks/useUserData";

import CommonPage from "../../components/common-page";
import HighlightsSkeleton from "./skeleton";
import RunsDrawer from "./components/RunsDrawer";
import DateRangeFilter from "./components/DateRangeFilter";
import {
  Toolbar,
  TabRow,
  EmptyState,
  StatusPill,
  SubtleText,
  FeedList,
  RowWrap,
  RowMain,
  BodyCol,
  BodyTop,
  CardFooter,
  ThumbWrap,
  ThumbEmpty,
  ControlButton,
  ProgressTrack,
  ProgressLine,
  ProgressFill,
  DurationBadge,
  RowText,
  TitleLine,
  FeedTitle,
  TitleToggle,
  ToggleSeg,
  Snippet,
  MetaLine,
  ShowChip,
  RowActions,
  MergeWarning,
  MergedBadge,
  MergePanel,
  MergePanelInfo,
} from "./style";

// Generation lifecycle chip on a picked card (running/failed; done renders as
// the "view article" button instead of a chip).
const GENERATION_COLORS = {
  running: { bg: "#D9C2FF", fg: "#3F1470" },
  failed: { bg: "#FFC2C2", fg: "#6B1414" },
};

const GENERATION_ICONS = {
  running: HourglassEmptyRoundedIcon,
  failed: ErrorOutlineRoundedIcon,
};

// Two tabs, 1:1 with status — a picked clip stays on Picked through its whole
// generation lifecycle, so search/tab-hunting can't "lose" it mid-flight.
const TABS = [
  { value: "pending", label: "pending" },
  { value: "picked", label: "picked" },
];

const PAGE_SIZE = 20;

// Fuzzy show-name matching for the filter's type-ahead. Each whitespace token
// must appear as an ordered subsequence of the show name (case-insensitive) —
// so "חד" matches "חדשות 13" and "or guy" matches "אורלי וגיא" only via its
// chars, while typos like "אורי" still surface "אורלי". Works for Hebrew too.
function isSubsequence(needle, haystack) {
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return needle.length === 0;
}

function fuzzyShowFilter(options, { inputValue }) {
  const tokens = (inputValue || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return options;
  return options.filter((opt) => {
    const o = (opt || "").toLowerCase();
    return tokens.every((t) => isSubsequence(t, o));
  });
}

function fmtTime24(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function fmtDuration(s) {
  if (typeof s !== "number" || !isFinite(s) || s <= 0) return "";
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  const r = total % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

// Compact Israel date "DD/MM" for the meta line.
function fmtDateIL(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
  });
}

// Stable per-show pill color so clips from the same program read as related.
function showColor(name) {
  if (!name) return { bg: "#eef3fb", fg: "#0f4c8a" };
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return { bg: `hsl(${h} 70% 94%)`, fg: `hsl(${h} 55% 30%)` };
}

// Inline clip preview: poster frame at rest, muted preview on hover, full
// playback with controls + sound on click — all in place, no navigation.
// Active playback is owned by the parent (one clip plays at a time), so playing
// one pauses the others and the play/pause icons never desync across rows.
function ClipThumb({ url, duration, isActive, onActivate, onStop }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  // When another row takes over (isActive → false), stop and reset this clip.
  useEffect(() => {
    if (!isActive && ref.current) {
      ref.current.pause();
      try { ref.current.currentTime = 0; } catch (_) { /* not yet seekable */ }
      setProgress(0);
    }
  }, [isActive]);

  // Click toggles play/pause with sound — no native controls (those render a
  // clunky scrubber + overflow menu in a thumbnail). A slim bar shows progress.
  const toggle = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    if (isActive) {
      v.pause();
      onStop();
    } else {
      v.muted = false;
      v.play().catch(() => {});
      onActivate();
    }
  };
  const preview = () => {
    if (isActive) return;
    const v = ref.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  };
  const stopPreview = () => {
    if (isActive) return;
    const v = ref.current;
    if (v) {
      v.pause();
      try { v.currentTime = 0; } catch (_) { /* not yet seekable */ }
      setProgress(0);
    }
  };
  const onTime = () => {
    const v = ref.current;
    if (v && v.duration) setProgress(v.currentTime / v.duration);
  };
  const onEnded = () => {
    onStop();
    setProgress(0);
  };
  // Seek by clicking/dragging the scrubber. stopPropagation so it doesn't also
  // toggle play/pause on the thumbnail.
  const seekFromEvent = (e) => {
    const v = ref.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = frac * v.duration;
    setProgress(frac);
  };
  const onScrubClick = (e) => {
    e.stopPropagation();
    seekFromEvent(e);
  };
  const onScrubMove = (e) => {
    if (e.buttons === 1) {
      e.stopPropagation();
      seekFromEvent(e);
    }
  };

  if (!url) {
    return (
      <ThumbWrap onClick={(e) => e.stopPropagation()}>
        <ThumbEmpty>—</ThumbEmpty>
        {duration ? <DurationBadge>{duration}</DurationBadge> : null}
      </ThumbWrap>
    );
  }
  return (
    <ThumbWrap onClick={toggle} onMouseEnter={preview} onMouseLeave={stopPreview}>
      <video
        ref={ref}
        src={url}
        preload="metadata"
        playsInline
        muted
        onTimeUpdate={onTime}
        onEnded={onEnded}
      />
      <ControlButton>
        {isActive ? (
          <PauseRoundedIcon fontSize="inherit" />
        ) : (
          <PlayArrowRoundedIcon fontSize="inherit" />
        )}
      </ControlButton>
      {duration ? <DurationBadge>{duration}</DurationBadge> : null}
      {isActive || progress > 0 ? (
        <ProgressTrack
          onClick={onScrubClick}
          onMouseMove={onScrubMove}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ProgressLine>
            <ProgressFill style={{ transform: `scaleX(${progress})` }} />
          </ProgressLine>
        </ProgressTrack>
      ) : null}
    </ThumbWrap>
  );
}

function HighlightRow({ item, text, isAdmin, onAction, onChooseTitle, onMerge, fetchHighlight, activeClipId, setActiveClipId, onCopyLink }) {
  const [busy, setBusy] = useState(false);
  const [merging, setMerging] = useState(false);
  const doMerge = async (e) => {
    e.stopPropagation();
    setMerging(true);
    try {
      await onMerge(item);
    } finally {
      setMerging(false);
    }
  };

  // Possible-duplicate warning: expand to fetch + review the suggested clip
  // before merging. The pair's clip isn't stored on the hint (signed URL would
  // expire), so it's fetched on first expand.
  const [expanded, setExpanded] = useState(false);
  const [pair, setPair] = useState(null);
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState(false);
  const [pairActive, setPairActive] = useState(false);
  const toggleExpand = async (e) => {
    e.stopPropagation();
    const next = !expanded;
    setExpanded(next);
    if (next && !pair && item.suggested_merge) {
      setPairLoading(true);
      setPairError(false);
      try {
        setPair(await fetchHighlight(item.suggested_merge.id));
      } catch (_) {
        setPairError(true);
      } finally {
        setPairLoading(false);
      }
    }
  };
  const status = item.status || "pending";
  const generation = item.generation;
  const GenIcon = GENERATION_ICONS[generation];
  const duration =
    item.clip_end_s != null && item.clip_start_s != null
      ? fmtDuration(item.clip_end_s - item.clip_start_s)
      : "";
  // The row shows whichever title is currently applied to the article.
  const displayTitle =
    item.title_choice === "candidate"
      ? item.title
      : item.generated_title || item.title;
  // Fallback so a missing i18n key can never crash the whole row.
  const tc = text.titleChoice || { label: "Article title", engine: "Final", candidate: "Draft" };
  const titleChoice = item.title_choice || "engine";
  const showTitleChooser = generation === "done" && !!item.generated_title;

  const act = (action) => async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await onAction(item, action);
    } finally {
      setBusy(false);
    }
  };

  const handleView = (e) => {
    e.stopPropagation();
    window.history.pushState({}, "", `/editor/${item.content_id}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  // Pick is the one editorial action — it selects the clip AND starts article
  // generation. A picked card shows its generation state inline (chip below)
  // and never leaves the Picked tab.
  const actions = busy ? (
    <Button size="small" disabled startIcon={<CircularProgress size={14} />}>
      {text.actions.working}
    </Button>
  ) : status === "pending" ? (
    <Button
      size="small"
      variant="contained"
      onClick={act("pick")}
      startIcon={<AutoAwesomeRoundedIcon fontSize="small" />}
    >
      {text.actions.pick}
    </Button>
  ) : (
    <>
      <Tooltip title={text.actions.unpick} arrow>
        <IconButton size="small" onClick={act("unpick")}>
          <UndoRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Retry re-runs v2a (billable) — admin-only, mirroring the queue
          manager. The server enforces this too; this only hides the button. */}
      {generation === "failed" && isAdmin && (
        <Button size="small" variant="outlined" onClick={act("generate")} sx={{ ml: 0.5 }}>
          {text.actions.retry}
        </Button>
      )}
      {generation === "done" && item.content_id && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewRoundedIcon fontSize="small" />}
          onClick={handleView}
          sx={{ ml: 0.5 }}
        >
          {text.actions.viewArticle}
        </Button>
      )}
    </>
  );

  const sc = showColor(item.show_title);

  return (
    <RowWrap id={`hl-${item.id}`}>
      <RowMain>
        <ClipThumb
          url={item.clip_url}
          duration={duration}
          isActive={activeClipId === item.id}
          onActivate={() => setActiveClipId(item.id)}
          onStop={() => setActiveClipId(null)}
        />
        <BodyCol>
          <BodyTop>
            <RowText>
              <TitleLine>
                <FeedTitle title={displayTitle}>{displayTitle}</FeedTitle>
                {/* Title-source switch sits right beside the title it controls. */}
                {showTitleChooser ? (
                  <Tooltip title={tc.label} arrow placement="top">
                    <TitleToggle>
                      <ToggleSeg
                        $active={titleChoice === "engine"}
                        onClick={() => onChooseTitle(item, "engine")}
                      >
                        {tc.engine}
                      </ToggleSeg>
                      <ToggleSeg
                        $active={titleChoice === "candidate"}
                        onClick={() => onChooseTitle(item, "candidate")}
                      >
                        {tc.candidate}
                      </ToggleSeg>
                    </TitleToggle>
                  </Tooltip>
                ) : null}
              </TitleLine>
              {item.summary ? <Snippet>{item.summary}</Snippet> : null}
            </RowText>
            <RowActions dir="ltr">
              <Tooltip title={text.copyLink} arrow placement="top">
                <IconButton size="small" onClick={() => onCopyLink(item)}>
                  <LinkRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {actions}
            </RowActions>
          </BodyTop>
          {/* Footer line — beside the thumbnail (no extra card height): merge
              pill anchored at the body's left edge, show tag + time at the right. */}
          {(generation === "running" || generation === "failed" || item.show_title || item.clip_start_utc_ms || item.suggested_merge || item.merged_from?.length) ? (
            <CardFooter>
              <MetaLine>
                {(generation === "running" || generation === "failed") && (
                  <StatusPill
                    dir="ltr"
                    $color={GENERATION_COLORS[generation].bg}
                    $textColor={GENERATION_COLORS[generation].fg}
                  >
                    <GenIcon
                      fontSize="inherit"
                      style={{ verticalAlign: "middle", marginRight: 4 }}
                    />
                    {text.generation?.[generation] || generation}
                  </StatusPill>
                )}
                {item.show_title ? (
                  <ShowChip $bg={sc.bg} $fg={sc.fg}>{item.show_title}</ShowChip>
                ) : null}
                {item.clip_start_utc_ms ? (
                  <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                    {fmtDateIL(item.clip_start_utc_ms)} · {fmtTime24(item.clip_start_utc_ms)}
                  </span>
                ) : null}
              </MetaLine>
              {item.suggested_merge ? (
                <MergeWarning onClick={toggleExpand} aria-expanded={expanded}>
                  <WarningAmberRoundedIcon />
                  {text.merge?.warn}
                  <ExpandMoreRoundedIcon
                    className={`chev${expanded ? " open" : ""}`}
                  />
                </MergeWarning>
              ) : item.merged_from?.length ? (
                <Tooltip title={text.merge?.merged} arrow placement="top">
                  <MergedBadge>
                    <CallMergeRoundedIcon />
                    {text.merge?.mergedFrom} {item.merged_from.length}{" "}
                    {text.merge?.clips}
                  </MergedBadge>
                </Tooltip>
              ) : null}
            </CardFooter>
          ) : null}
        </BodyCol>
      </RowMain>
      {/* Review panel — the suggested clip to merge, revealed under the card. */}
      {item.suggested_merge ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <MergePanel onClick={(e) => e.stopPropagation()}>
            {pairLoading ? (
              <CircularProgress size={22} sx={{ m: "auto" }} />
            ) : pairError || !pair ? (
              <span style={{ margin: "auto", fontSize: 13, opacity: 0.7 }}>
                {text.merge?.loadError}
              </span>
            ) : (
              <>
                <ClipThumb
                  url={pair.clip_url}
                  duration={
                    pair.clip_end_s != null && pair.clip_start_s != null
                      ? fmtDuration(pair.clip_end_s - pair.clip_start_s)
                      : ""
                  }
                  isActive={pairActive}
                  onActivate={() => setPairActive(true)}
                  onStop={() => setPairActive(false)}
                />
                <MergePanelInfo>
                  <span className="ptitle">{pair.title}</span>
                  {pair.summary ? (
                    <span className="psummary">{pair.summary}</span>
                  ) : null}
                  {pair.show_title || pair.clip_start_utc_ms ? (
                    <span className="pmeta">
                      {pair.show_title ? (
                        <ShowChip
                          $bg={showColor(pair.show_title).bg}
                          $fg={showColor(pair.show_title).fg}
                        >
                          {pair.show_title}
                        </ShowChip>
                      ) : null}
                      {pair.clip_start_utc_ms ? (
                        <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                          {fmtDateIL(pair.clip_start_utc_ms)} ·{" "}
                          {fmtTime24(pair.clip_start_utc_ms)}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </MergePanelInfo>
                <Button
                  variant="contained"
                  disabled={merging}
                  startIcon={
                    merging ? (
                      <CircularProgress size={14} />
                    ) : (
                      <CallMergeRoundedIcon fontSize="small" />
                    )
                  }
                  onClick={doMerge}
                >
                  {merging ? text.merge?.merging : text.merge?.action}
                </Button>
              </>
            )}
          </MergePanel>
        </Collapse>
      ) : null}
    </RowWrap>
  );
}

export default function Highlights() {
  const [
    {
      pages: { highlights: text },
    },
  ] = useLanguage();
  const {
    listHighlights,
    listRuns,
    getHighlight,
    pickHighlight,
    unpickHighlight,
    generateHighlight,
    chooseHighlightTitle,
    mergeHighlight,
  } = useHighlights();
  const { addToaster } = useToaster();
  const isMobile = useMediaQuery();
  const currentUser = useUserData();
  const isAdmin = currentUser?.role === "admin";

  const [items, setItems] = useState([]); // the current page only (server-paginated)
  const [total, setTotal] = useState(0); // rows matching tab + shows + date (pager count)
  const [counts, setCounts] = useState({ pending: 0, picked: 0 });
  const [shows, setShows] = useState([]); // distinct show titles (dropdown), server-computed
  const [activeClipId, setActiveClipId] = useState(null);
  const [tab, setTab] = useState("pending");
  // Empty = all shows. Holds the explicit subset the editor typed/picked.
  const [selectedShows, setSelectedShows] = useState([]);
  // Free-text search over title/summary/show. `searchInput` is the raw box value;
  // `search` is the debounced value actually sent to the server (see effect below).
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [rtlMemo, setRtlMemo] = useMemorize("highlights-rtl");
  // Hebrew content → RTL by default; only LTR if the user explicitly turned it off.
  const rtl = rtlMemo !== "0";
  // Date filter as a preset-or-custom range; from/to are Israel-local datetime
  // keys (or "") sent to the server, which compares them against each clip's
  // Israel-local air time.
  const [dateRange, setDateRange] = useState({ preset: "all", from: "", to: "" });
  const [page, setPage] = useState(0);
  // The feed scrolls internally (sticky toolbar), so on a page change we must
  // reset it to the top — otherwise paging leaves you at the bottom of the new
  // page (looks like nothing happened).
  const feedRef = useRef(null);
  // Deep-link target (?id=): opens that ONE candidate in a focused view (below),
  // instead of trying to scroll to it inside the filtered/paginated list.
  const [targetId, setTargetId] = useState(() =>
    new URLSearchParams(window.location.search).get("id"),
  );
  // Re-read ?id= on every navigation — a persisted highlights page (Reshet WP
  // iframe) otherwise ignores a deep-link opened after mount and stays on the list.
  useEffect(() => {
    const sync = () =>
      setTargetId(new URLSearchParams(window.location.search).get("id"));
    window.addEventListener("locationchange", sync);
    return () => window.removeEventListener("locationchange", sync);
  }, []);
  const [focused, setFocused] = useState(null);
  const [focusedState, setFocusedState] = useState("idle"); // idle|loading|ready|error
  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const titleText = useMemo(
    () => (isMobile ? text.inPageTitle.short : text.inPageTitle.long),
    [isMobile, text],
  );

  // One page of the CURRENT tab + filters. The server does the filtering,
  // sorting, and pagination and returns per-tab `counts` (badges, computed with
  // the active filters so a search shows hits on BOTH tabs) and the full
  // `shows` list (dropdown) — so we never load the whole table (that used to
  // ship ~2.9MB and feel hung).
  // Requests are sequence-guarded: the effect refires on every tab/filter/page
  // change plus the generation poll, and two in-flight responses can resolve
  // out of order — only the latest request may touch the UI.
  const fetchSeqRef = useRef(0);
  const fetchItems = async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const response = await listHighlights({
        tab,
        shows: selectedShows,
        from: dateRange.from || undefined,
        to: dateRange.to || undefined,
        search: search || undefined,
        page: page + 1, // server is 1-based; TablePagination is 0-based
        pageSize: PAGE_SIZE,
      });
      if (seq !== fetchSeqRef.current) return; // superseded — drop stale result
      setItems(response?.items || []);
      setTotal(response?.total || 0);
      if (response?.counts) setCounts(response.counts);
      if (response?.shows) setShows(response.shows);
    } catch (_) {
      if (seq !== fetchSeqRef.current) return;
      setItems([]);
      setTotal(0);
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  const handleRefresh = async () => {
    await fetchItems();
    addToaster({ message: text.refreshed, severity: "success" });
  };

  const openRuns = async () => {
    setRunsOpen(true);
    setRunsLoading(true);
    try {
      const response = await listRuns();
      setRuns(response?.items || []);
    } catch (_) {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  };

  const ACTIONS = {
    // A re-pick of a clip whose article already exists doesn't re-generate —
    // tell the editor which of the two happened.
    pick: {
      call: pickHighlight,
      toast: (resp) =>
        resp?.highlight?.generation === "done" ? text.pickedAgain : text.picked,
    },
    unpick: { call: unpickHighlight, toast: () => text.unpicked },
    generate: { call: generateHighlight, toast: () => text.generateQueued },
  };

  const handleAction = async (item, action) => {
    try {
      const response = await ACTIONS[action].call(item.id);
      addToaster({ message: ACTIONS[action].toast(response), severity: "success" });
      // Pick/unpick move the row between the two tabs — refetch the page rather
      // than patch it in place (which would leave a stale row on a tab it no
      // longer belongs to) and to keep the badge counts accurate.
      await fetchItems();
    } catch (_) { /* toaster raised by hook */ }
  };

  const handleChooseTitle = async (item, choice) => {
    if (choice === (item.title_choice || "engine")) return;
    const prevChoice = item.title_choice;
    // Optimistic: flip the displayed title immediately; the server round-trip
    // (which rewrites the article title) happens in the background.
    setItems((cur) =>
      cur.map((i) => (i.id === item.id ? { ...i, title_choice: choice } : i)),
    );
    try {
      const response = await chooseHighlightTitle(item.id, choice);
      if (response?.highlight) {
        setItems((cur) =>
          cur.map((i) => (i.id === item.id ? response.highlight : i)),
        );
      }
    } catch (_) {
      // Revert on failure (hook already raised an error toast).
      setItems((cur) =>
        cur.map((i) => (i.id === item.id ? { ...i, title_choice: prevChoice } : i)),
      );
    }
  };

  const handleMerge = async (item) => {
    if (!item.suggested_merge) return;
    try {
      await mergeHighlight(item.id, item.suggested_merge.id);
      addToaster({ message: text.merge.merged, severity: "success" });
      // The merged candidate is new and both originals are now trashed — refetch.
      await fetchItems();
    } catch (_) { /* toaster raised by hook */ }
  };

  // Fetch the single linked candidate whenever a ?id= target is present, so a
  // shared link resolves that exact one regardless of tab, page, or load window.
  useEffect(() => {
    if (!targetId) { setFocused(null); setFocusedState("idle"); return; }
    let alive = true;
    setFocusedState("loading");
    getHighlight(targetId)
      .then((h) => { if (alive) { setFocused(h || null); setFocusedState(h ? "ready" : "error"); } })
      .catch(() => { if (alive) { setFocused(null); setFocusedState("error"); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  // Leave the focused view and return to the full list.
  const clearFocus = () => {
    window.history.pushState({}, "", "/highlights");
    setTargetId(null);
  };

  // Focused-view handlers sync the single row straight from server responses —
  // the row may be past the list's load window, so patching `items` won't reach it.
  const focusedAction = async (item, action) => {
    try {
      const response = await ACTIONS[action].call(item.id);
      addToaster({ message: ACTIONS[action].toast(), severity: "success" });
      if (response?.highlight) setFocused(response.highlight);
      else { const h = await getHighlight(item.id).catch(() => null); if (h) setFocused(h); }
    } catch (_) { /* toaster raised by hook */ }
  };
  const focusedChooseTitle = async (item, choice) => {
    if (choice === (item.title_choice || "engine")) return;
    const prev = item.title_choice;
    setFocused((f) => (f ? { ...f, title_choice: choice } : f));
    try {
      const response = await chooseHighlightTitle(item.id, choice);
      if (response?.highlight) setFocused(response.highlight);
    } catch (_) {
      setFocused((f) => (f ? { ...f, title_choice: prev } : f));
    }
  };
  const focusedMerge = async (item) => {
    if (!item.suggested_merge) return;
    try {
      await mergeHighlight(item.id, item.suggested_merge.id);
      addToaster({ message: text.merge.merged, severity: "success" });
      clearFocus(); // both originals are now trashed — return to the list
      await fetchItems();
    } catch (_) { /* toaster raised by hook */ }
  };

  // Fetch the current page whenever the tab, filters, or page change (this also
  // does the initial load). The change handlers reset to page 0 so a filter can
  // never strand you on a now-empty page.
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedShows, dateRange, search, page]);

  // Debounce the search box so we don't fire a request per keystroke. Committing
  // the value goes through setFilter, which resets to page 0 like the other filters.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === search) return;
    const t = setTimeout(() => setFilter(() => setSearch(trimmed)), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // While a row on the current page is still generating, refetch once a minute
  // so its chip flips to the article link (or failed) without a manual refresh.
  const hasGenerating = items.some((i) => i.generation === "running");
  useEffect(() => {
    if (!hasGenerating) return;
    const interval = setInterval(() => { fetchItems(); }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGenerating]);

  // Filtering, sorting, counts, and the show list all come from the server now
  // (see fetchItems): `items` is already this tab's page, `counts` drives the tab
  // badges, `shows` drives the dropdown. Changing the tab/filter resets to page 0.
  const setFilter = (fn) => { fn(); setPage(0); };

  // With a search/filter active a bare "no pending candidates" would read as
  // data loss — say explicitly that the FILTER matched nothing.
  const filtersActive =
    !!search || selectedShows.length > 0 || !!dateRange.from || !!dateRange.to;
  const emptyText = filtersActive ? text.empty.search : text.empty[tab];

  // ── Deep-linking (?id=) ────────────────────────────────────────────────────
  // The copied link opens the target candidate on its own (focused view above),
  // so there's no scroll-into-list / tab-hunt / page-jump machinery to get wrong.
  const copyLink = (item) => {
    navigator.clipboard?.writeText(
      `${window.location.origin}/highlights?id=${item.id}`,
    );
    addToaster({ message: text.linkCopied, severity: "success" });
  };

  // On a page change, snap the internally-scrolling feed back to the top so a new
  // page starts at row 1 instead of wherever the previous page was scrolled to.
  useEffect(() => {
    feedRef.current?.scrollTo({ top: 0 });
  }, [page]);

  if (initialLoad && loading) return <HighlightsSkeleton />;

  // ── Focused single-candidate view (deep-link ?id=) ──────────────────────────
  // A shared link resolves the ONE linked candidate by id and shows it alone,
  // so it's never confused for a neighbour in the filtered/paginated list.
  if (targetId) {
    return (
      <CommonPage
        title={titleText}
        subtitle={text.subtitle}
        action={{
          label: text.backToAll,
          icon: <ArrowBackRoundedIcon fontSize="small" />,
          onClick: clearFocus,
        }}
      >
        {focusedState === "loading" ? (
          <HighlightsSkeleton />
        ) : focused ? (
          <FeedList dir={rtl ? "rtl" : "ltr"}>
            <HighlightRow
              item={focused}
              text={text}
              isAdmin={isAdmin}
              onAction={focusedAction}
              onChooseTitle={focusedChooseTitle}
              onMerge={focusedMerge}
              fetchHighlight={getHighlight}
              activeClipId={activeClipId}
              setActiveClipId={setActiveClipId}
              onCopyLink={copyLink}
            />
          </FeedList>
        ) : (
          <EmptyState>
            <Typography variant="h6">{text.focusNotFound.title}</Typography>
            <Typography variant="body2">{text.focusNotFound.subtitle}</Typography>
          </EmptyState>
        )}
      </CommonPage>
    );
  }

  return (
    <CommonPage
      title={titleText}
      subtitle={text.subtitle}
      action={{
        label: text.refresh,
        icon: <RefreshRoundedIcon fontSize="small" />,
        onClick: handleRefresh,
        disabled: loading,
      }}
    >
      <Toolbar>
        <TabRow>
          {TABS.map((t) => (
            <Button
              key={t.value}
              size="small"
              variant={tab === t.value ? "contained" : "outlined"}
              onClick={() => setFilter(() => setTab(t.value))}
            >
              {text.tabs[t.label]}
              <span
                style={{
                  marginInlineStart: 6,
                  opacity: 0.6,
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {counts[t.value]}
              </span>
            </Button>
          ))}
        </TabRow>
        <Autocomplete
          multiple
          size="small"
          options={shows}
          value={selectedShows}
          onChange={(_, v) => setFilter(() => setSelectedShows(v))}
          filterOptions={fuzzyShowFilter}
          limitTags={2}
          disableCloseOnSelect
          ChipProps={{ size: "small" }}
          sx={{ minWidth: 240, maxWidth: 380, flex: "1 1 240px" }}
          noOptionsText={text.filter.noMatch}
          renderInput={(params) => (
            <TextField
              {...params}
              label={text.filter.show}
              placeholder={selectedShows.length === 0 ? text.filter.all : undefined}
            />
          )}
        />
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          label={text.filter.search}
          placeholder={text.filter.searchPlaceholder}
          sx={{ minWidth: 200, maxWidth: 320, flex: "1 1 200px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={() => setSearchInput("")}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <DateRangeFilter
          value={dateRange}
          onChange={(v) => setFilter(() => setDateRange(v))}
          text={text.filter.date}
        />
        <Tooltip title={text.runs.tooltip} arrow>
          <IconButton size="small" sx={{ marginLeft: "auto" }} onClick={openRuns}>
            <ReceiptLongRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <FormControlLabel
          sx={{ marginRight: 0 }}
          control={
            <Switch
              size="small"
              checked={rtl}
              onChange={() => setRtlMemo(rtl ? "0" : "1")}
            />
          }
          label={<SubtleText>{text.rtlToggle}</SubtleText>}
        />
        <SubtleText>{total}</SubtleText>
      </Toolbar>

      {total === 0 ? (
        <EmptyState>
          <Typography variant="h6">{emptyText?.title}</Typography>
          <Typography variant="body2">{emptyText?.subtitle}</Typography>
        </EmptyState>
      ) : (
        <>
          <FeedList ref={feedRef} dir={rtl ? "rtl" : "ltr"}>
            {items.map((item) => (
              <HighlightRow
                key={item.id}
                item={item}
                text={text}
                isAdmin={isAdmin}
                onAction={handleAction}
                onChooseTitle={handleChooseTitle}
                onMerge={handleMerge}
                fetchHighlight={getHighlight}
                activeClipId={activeClipId}
                setActiveClipId={setActiveClipId}
                onCopyLink={copyLink}
              />
            ))}
          </FeedList>
          {total > PAGE_SIZE && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              // The feed above is `flex: 1` and was crushing the pager to a ~13px
              // sliver (its controls clipped, so it looked missing). Keep its full
              // height and pin it as a visible footer below the scrolling list.
              sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider" }}
            />
          )}
        </>
      )}

      <RunsDrawer
        open={runsOpen}
        onClose={() => setRunsOpen(false)}
        runs={runs}
        loading={runsLoading}
        text={text}
        rtl={rtl}
      />
    </CommonPage>
  );
}
